import { Link, TableCell, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import {
  createSearchRequestProvider,
  getActiveFiltersCount,
  preprocessSearchRequest,
  withoutBaseTableModel,
} from "~/components/Table";
import {
  commonResourceColumns,
  selectCountableListResponse,
} from "~/domain/common";
import type { LqsColumn, ObjectStore } from "~/lqs";
import {
  useLqsVersion,
  objectStoreVersionHistories,
  LqsResourceTable,
  useObjectStores,
} from "~/lqs";
import { useLqsNavigator } from "~/paths";
import { listObjectStoresSchema } from "../schemas";

function ObjectStorePrimaryCell({
  id,
  readableName,
}: {
  id: string;
  readableName: string;
}) {
  const lqsNavigator = useLqsNavigator({ toObjectStoreDetails: true });

  return (
    <TableCell>
      {readableName}
      <Link
        component={RouterLink}
        to={lqsNavigator.toObjectStoreDetails({
          objectStoreId: id,
        })}
      >
        <Typography variant="body2">{id}</Typography>
      </Link>
    </TableCell>
  );
}

function ObjectStoreNameCell({ objectStore }: { objectStore: ObjectStore }) {
  return (
    <ObjectStorePrimaryCell
      id={objectStore.id}
      readableName={objectStore.name}
    />
  );
}

function ObjectStoreBucketNameCell({
  objectStore,
}: {
  objectStore: ObjectStore;
}) {
  const lqsVersion = useLqsVersion();
  if (
    lqsVersion.checkStatus(objectStoreVersionHistories.model.name) !==
    "unavailable"
  ) {
    return <TableCell>{objectStore.bucketName}</TableCell>;
  } else {
    return (
      <ObjectStorePrimaryCell
        id={objectStore.id}
        readableName={objectStore.bucketName}
      />
    );
  }
}

const columns: ReadonlyArray<LqsColumn<"objectStore">> = [
  {
    header: "Name",
    sortKey: "name",
    versionHistory: objectStoreVersionHistories.model.name,
    renderCell: (objectStore) => (
      <ObjectStoreNameCell objectStore={objectStore} />
    ),
  },
  {
    header: "Bucket Name",
    sortKey: "bucketName",
    renderCell: (objectStore) => (
      <ObjectStoreBucketNameCell objectStore={objectStore} />
    ),
  },
  {
    accessor: "keyPrefix",
    dataType: "text",
    isSortable: true,
    defaultHidden: true,
  },
  {
    accessor: "accessKeyId",
    dataType: "text",
    isSortable: true,
  },
  {
    accessor: "regionName",
    dataType: "text",
    isSortable: true,
  },
  {
    accessor: "endpointUrl",
    dataType: "text",
    isSortable: true,
  },
  {
    accessor: "_default",
    dataType: "boolean",
    isSortable: true,
    defaultHidden: true,
  },
  {
    accessor: "readOnly",
    dataType: "boolean",
    isSortable: true,
    defaultHidden: true,
  },
  {
    accessor: "managed",
    dataType: "boolean",
    isSortable: true,
    defaultHidden: true,
  },
  {
    accessor: "disabled",
    dataType: "boolean",
    isSortable: true,
    defaultHidden: true,
  },
  {
    accessor: "note",
    dataType: "text",
    isSortable: true,
  },
  ...commonResourceColumns,
];

export const [useObjectStoreSearchRequest, ObjectStoreSearchRequestProvider] =
  createSearchRequestProvider({
    columns,
    filterSchema: listObjectStoresSchema,
  });

export function ObjectStoreTable() {
  const [request, setRequest] = useObjectStoreSearchRequest();

  const lqsNavigator = useLqsNavigator({ toObjectStoreCreate: true });

  const searchQuery = useObjectStores(preprocessSearchRequest(request), {
    keepPreviousData: true,
    cacheTime: 0,
    select: selectCountableListResponse,
  });

  const filterValues = withoutBaseTableModel(request);

  return (
    <LqsResourceTable
      resourceName="object store"
      registryKey="objectStore"
      resourceCreateLocation={lqsNavigator.toObjectStoreCreate()}
      getRowKey={(objectStore) => objectStore.id}
      columns={columns}
      searchQuery={searchQuery}
      tableModel={request}
      onTableModelChange={setRequest}
      activeFilterCount={getActiveFiltersCount(filterValues)}
    />
  );
}
